import { ViewIds } from '@ardoq/api-types';
import {
  persistentReducedStream,
  streamReducer,
  toPersistentStream,
} from '@ardoq/rxbeach';
import { distinctUntilChanged, map } from 'rxjs';
import { pick } from 'lodash';
import { shallowEqual } from 'utils/objectUtils';
import { MAIN_PANE_LEFT, ViewSettings } from './types';
import { workspaceModeSettingsForAllViews$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { viewpointModeSettingsForAllViews$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import {
  ViewpointModeViewSettingsWithChanges,
  WorkspaceModeViewSettingsWithChanges,
} from 'views/environmentals/viewSettings/types';
import {
  DEFAULT_VIEWPOINT_MODE_SETTINGS,
  DEFAULT_WORKSPACE_MODE_SETTINGS,
} from 'views/environmentals/viewSettings/defaultStreamState';
import {
  isViewpointModeView,
  isViewSettingsUpdateRelevantForInstance,
  isWorkspaceModeView,
} from 'views/environmentals/viewSettings/utils';
import {
  toViewpointModeMainViewInstanceId,
  toWorkspaceModeMainViewInstanceId,
} from 'appLayout/mainViewInstanceIds';
import {
  ViewpointModeTab,
  viewpointModeTab$,
} from 'appLayout/viewpointModeTab$';
import {
  leftWorkspaceModeViewPaneTab$,
  rightWorkspaceModeViewPaneTab$,
  WorkspaceModeViewPaneTab,
} from 'appLayout/workspaceModeViewPaneTabs$';
import { RequestedTabs, requestedTabs$ } from 'appLayout/requestedTabs$';

export type ActiveViews = {
  mainViewId: ViewIds;
  secondaryViewId: ViewIds | null;
};

export type ActiveViewState = ActiveViews & {
  mainViewInstanceId: string | null;
  mainViewSettings: ViewSettings;
};

export type ActiveViewStreamState = ActiveViewState & {
  isViewpointMode: boolean;
  viewpointModeGlobalSettings: ViewpointModeViewSettingsWithChanges['currentSettings'];
  workspaceModeGlobalSettings: WorkspaceModeViewSettingsWithChanges['currentSettings'];
};

export const DEFAULT_ACTIVE_VIEW_STATE: ActiveViewState = {
  mainViewId: ViewIds.NONE,
  mainViewInstanceId: null,
  secondaryViewId: null,
  mainViewSettings: {},
};

export const DEFAULT_ACTIVE_VIEW_STREAM_STATE: ActiveViewStreamState = {
  ...DEFAULT_ACTIVE_VIEW_STATE,
  isViewpointMode: false,
  viewpointModeGlobalSettings: DEFAULT_VIEWPOINT_MODE_SETTINGS,
  workspaceModeGlobalSettings: DEFAULT_WORKSPACE_MODE_SETTINGS,
};

const isUpdateForMainView = (
  { mainViewId, mainViewInstanceId }: ActiveViewState,
  { viewId, viewInstanceId }: { viewId?: ViewIds; viewInstanceId?: string }
): boolean =>
  mainViewInstanceId !== null &&
  isViewSettingsUpdateRelevantForInstance({
    subscriberViewId: mainViewId,
    subscriberViewInstanceId: mainViewInstanceId,
    updatedViewId: viewId,
    updatedViewInstanceId: viewInstanceId,
  });

export const storeViewpointModeSettingsUpdate = (
  state: ActiveViewStreamState,
  update: ViewpointModeViewSettingsWithChanges
): ActiveViewStreamState => {
  const { mainViewId } = state;
  const nextState = {
    ...state,
    viewpointModeGlobalSettings: update.currentSettings,
  };
  // Updates for other view instances must not leak into the main view settings.
  if (
    !state.isViewpointMode ||
    !isViewpointModeView(mainViewId) ||
    !isUpdateForMainView(state, update)
  ) {
    return nextState;
  }
  return {
    ...nextState,
    mainViewSettings: update.currentSettings[mainViewId],
  };
};

export const storeWorkspaceModeSettingsUpdate = (
  state: ActiveViewStreamState,
  update: WorkspaceModeViewSettingsWithChanges
): ActiveViewStreamState => {
  const { mainViewId } = state;
  const nextState = {
    ...state,
    workspaceModeGlobalSettings: update.currentSettings,
  };
  // Updates for other view instances, e.g. the right pane, must not leak into
  // the main view settings.
  if (
    state.isViewpointMode ||
    !isWorkspaceModeView(mainViewId) ||
    !isUpdateForMainView(state, update)
  ) {
    return nextState;
  }
  return {
    ...nextState,
    mainViewSettings: update.currentSettings[mainViewId],
  };
};

export const followMode = (
  state: ActiveViewStreamState,
  { isViewpointMode }: RequestedTabs
): ActiveViewStreamState => ({ ...state, isViewpointMode });

export const followViewpointModeTab = (
  state: ActiveViewStreamState,
  { activeTabId }: ViewpointModeTab
): ActiveViewStreamState => {
  if (activeTabId === ViewIds.NONE) {
    return { ...state, ...DEFAULT_ACTIVE_VIEW_STATE };
  }
  return {
    ...state,
    mainViewId: activeTabId,
    mainViewInstanceId: toViewpointModeMainViewInstanceId(activeTabId),
    secondaryViewId: null,
    mainViewSettings: state.viewpointModeGlobalSettings[activeTabId],
  };
};

export const followWorkspaceModeLeftTab = (
  state: ActiveViewStreamState,
  { activeTabId }: WorkspaceModeViewPaneTab
): ActiveViewStreamState => {
  // The viewpoint tab owns the main view in viewpoint mode.
  if (state.isViewpointMode) {
    return state;
  }
  if (activeTabId === ViewIds.NONE) {
    return {
      ...state,
      ...DEFAULT_ACTIVE_VIEW_STATE,
      secondaryViewId: state.secondaryViewId,
    };
  }
  // The tab re-emits the same view on metaInfo changes; re-reading the global
  // settings would pick up changes made by the right pane.
  if (activeTabId === state.mainViewId) {
    return state;
  }
  return {
    ...state,
    mainViewId: activeTabId,
    mainViewInstanceId: toWorkspaceModeMainViewInstanceId(
      MAIN_PANE_LEFT,
      activeTabId
    ),
    mainViewSettings: state.workspaceModeGlobalSettings[activeTabId],
  };
};

export const followWorkspaceModeRightTab = (
  state: ActiveViewStreamState,
  { activeTabId }: WorkspaceModeViewPaneTab
): ActiveViewStreamState => ({
  ...state,
  secondaryViewId: activeTabId === ViewIds.NONE ? null : activeTabId,
});

const _activeViewState$ = persistentReducedStream<ActiveViewStreamState>(
  '_activeViewState$',
  DEFAULT_ACTIVE_VIEW_STREAM_STATE,
  [
    streamReducer(
      viewpointModeSettingsForAllViews$,
      storeViewpointModeSettingsUpdate
    ),
    streamReducer(
      workspaceModeSettingsForAllViews$,
      storeWorkspaceModeSettingsUpdate
    ),
    streamReducer(requestedTabs$, followMode),
    // requestedTabs$ holds the requested views, not the mounted ones: the tab
    // streams drop views unavailable in the mode, and the workspace tabs
    // resolve start views, defaults and replacements.
    streamReducer(viewpointModeTab$, followViewpointModeTab),
    streamReducer(leftWorkspaceModeViewPaneTab$, followWorkspaceModeLeftTab),
    streamReducer(rightWorkspaceModeViewPaneTab$, followWorkspaceModeRightTab),
  ]
);

const activeViewState$ = toPersistentStream(
  'activeViewState$',
  _activeViewState$.pipe(
    map((state): ActiveViewState =>
      pick(state, [
        'mainViewId',
        'mainViewInstanceId',
        'secondaryViewId',
        'mainViewSettings',
      ])
    ),
    distinctUntilChanged<ActiveViewState>(shallowEqual)
  ),
  DEFAULT_ACTIVE_VIEW_STATE
);

const toActiveViews = ({
  mainViewId,
  secondaryViewId,
}: ActiveViewState): ActiveViews => ({ mainViewId, secondaryViewId });

const activeView$ = toPersistentStream(
  'activeView$',
  activeViewState$.pipe(
    map(toActiveViews),
    distinctUntilChanged<ActiveViews>(shallowEqual)
  ),
  toActiveViews(DEFAULT_ACTIVE_VIEW_STATE)
);

export { activeView$, activeViewState$ };
